import { useCallback, useEffect, useRef, useState } from "react";
import { capturePrivateSession, isPrivateSessionCurrent, subscribePrivateSession } from "@/lib/api/private-session";

const PREFERENCE_KEY = "seller.notification-sound";
type SoundStatus = "locked" | "ready" | "muted" | "unsupported";
const audioConstructor = () => window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
function enabledPreference() {
  try { return localStorage.getItem(PREFERENCE_KEY) !== "muted"; } catch { return true; }
}

/** Foreground feed audio only; browser/native push owns its own background sound. */
export function useNotificationSound(sellerId?: string, storeId?: string) {
  const [soundStatus, setStatus] = useState<SoundStatus>("locked");
  const controls = useRef<{ enable: () => Promise<void>; mute: () => void; notify: () => void } | null>(null);
  useEffect(() => {
    if (!sellerId || !storeId) return;
    const lifetime = capturePrivateSession();
    let active = true, enabled = enabledPreference();
    let audio: AudioContext | null = null;
    let tone: OscillatorNode | null = null;
    let lastAlert = -Infinity;
    const current = () => active && isPrivateSessionCurrent(lifetime);
    const status = () => {
      if (current()) setStatus(!audioConstructor() ? "unsupported" : !enabled ? "muted" : audio?.state === "running" ? "ready" : "locked");
    };
    const stop = () => {
      if (!tone) return;
      try { tone.stop(); } catch { /* Already ended. */ }
      tone.disconnect(); tone = null;
    };
    const play = () => {
      if (!current() || !enabled || audio?.state !== "running" || document.visibilityState === "hidden") return;
      try {
        stop();
        const oscillator = audio.createOscillator(), gain = audio.createGain(), now = audio.currentTime;
        oscillator.frequency.setValueAtTime(880, now);
        gain.gain.setValueAtTime(0.001, now);
        gain.gain.linearRampToValueAtTime(0.12, now + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
        oscillator.connect(gain); gain.connect(audio.destination);
        tone = oscillator;
        oscillator.onended = () => { oscillator.disconnect(); gain.disconnect(); if (tone === oscillator) tone = null; };
        oscillator.start(now); oscillator.stop(now + 0.23);
      } catch { status(); }
    };
    const save = () => {
      try { localStorage.setItem(PREFERENCE_KEY, enabled ? "enabled" : "muted"); } catch { /* Optional device storage. */ }
    };
    const owner = {
      enable: async () => {
        if (!current() || !audioConstructor()) return;
        enabled = true; save();
        try {
          if (!audio || audio.state === "closed") { audio = new (audioConstructor()!)(); audio.onstatechange = status; }
          await audio.resume();
          // A store/session change or mute during resume must never play a sample.
          if (current()) { status(); play(); }
        } catch { status(); }
      },
      mute: () => { if (current()) { enabled = false; save(); stop(); status(); } },
      notify: () => {
        if (!current() || !enabled || audio?.state !== "running" || document.visibilityState === "hidden") return;
        if (Date.now() - lastAlert < 1000) return;
        lastAlert = Date.now(); play();
      },
    };
    const close = () => { stop(); if (audio) { audio.onstatechange = null; void audio.close().catch(() => undefined); audio = null; } };
    controls.current = owner; status();
    const release = subscribePrivateSession(() => { if (!current()) close(); });
    const visibility = () => { if (document.visibilityState === "hidden") stop(); };
    document.addEventListener("visibilitychange", visibility);
    return () => { active = false; release(); document.removeEventListener("visibilitychange", visibility); if (controls.current === owner) controls.current = null; close(); };
  }, [sellerId, storeId]);
  const enableSound = useCallback(() => controls.current?.enable(), []);
  const muteSound = useCallback(() => controls.current?.mute(), []);
  const notifySound = useCallback(() => controls.current?.notify(), []);
  return { soundStatus, enableSound, muteSound, notifySound };
}
